var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { showCustomDialog, showErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { Box, Button, DialogActions, DialogContent, DialogTitle, Slider, Typography } from '@mui/material';
import React from 'react';
import { centerCrop, makeAspectCrop, ReactCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useCommonFormUiTranslation } from '../../hooks';
/**
 * Displays a crop editor dialog for an image, allowing the user to crop, optionally rotate,
 * and scale the image before saving. Supports fixed aspect ratio, circular cropping,
 * and free-form cropping.
 *
 * @param {CropParams} params - The crop editor configuration.
 * @param {string} params.imageData - Base64 string of the image to be cropped.
 * @param {number} [params.cropAspectRatio] - Predefined aspect ratio for cropping (e.g., 1 for square, 16/9 for landscape).
 * @param {boolean} [params.circularCrop] - If true, the crop area is displayed as a circle.
 * @param {boolean} [params.allowRotate] - If true, rotation controls are enabled.
 * @param {(data: string) => void} params.onSave - Callback fired when the crop is saved, with the cropped image data as Base64.
 * @param {() => void} [params.onCancel] - Callback fired when the crop dialog is canceled.
 * @returns {void}
 */
export const showCropEditorDialog = (params) => showCustomDialog({
    maxWidth: 'sm',
    fullWidth: false,
    content: (close) => (React.createElement(ImageCropEditorDialogContent, { imageData: params.imageData, cropAspectRatio: params.cropAspectRatio, circularCrop: params.circularCrop, allowRotate: params.allowRotate, onCancel: close, onSave: (data) => {
            params.onSave(data);
            close();
        } })),
});
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
    return centerCrop(makeAspectCrop({
        unit: '%',
        width: 90,
    }, aspect, mediaWidth, mediaHeight), mediaWidth, mediaHeight);
}
const ImageCropEditorDialogContent = (props) => {
    const imgRef = React.useRef(null);
    const [completedCrop, setCompletedCrop] = React.useState();
    const [scale, setScale] = React.useState(1);
    const [crop, setCrop] = React.useState();
    const [rotate, setRotate] = React.useState(0);
    const { t } = useCommonFormUiTranslation();
    function onImageLoad(e) {
        var _a;
        const { width, height } = e.currentTarget;
        setCrop(centerAspectCrop(width, height, (_a = props.cropAspectRatio) !== null && _a !== void 0 ? _a : 16 / 9));
    }
    function cropImage() {
        return __awaiter(this, void 0, void 0, function* () {
            const image = imgRef.current;
            if (!image || !completedCrop) {
                throw new Error(t('widget.widgetFile.cropCanvasError'));
            }
            if (completedCrop.width === 0 || completedCrop.height === 0) {
                showErrorDialog({ title: t('widget.widgetFile.selectImageUsingPointer'), onClose: () => undefined });
                return;
            }
            const TO_RADIANS = Math.PI / 180;
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;
            // devicePixelRatio slightly increases sharpness on retina devices
            // at the expense of slightly slower render times and needing to
            // size the image back down if you want to download/upload and be
            // true to the images natural size.
            const pixelRatio = window.devicePixelRatio;
            const offscreen = new OffscreenCanvas(Math.floor(completedCrop.width * scaleX * pixelRatio), Math.floor(completedCrop.height * scaleY * pixelRatio));
            const ctx = offscreen.getContext('2d');
            if (!ctx) {
                throw new Error('No 2d context');
            }
            ctx.scale(pixelRatio, pixelRatio);
            ctx.imageSmoothingQuality = 'high';
            const cropX = completedCrop.x * scaleX;
            const cropY = completedCrop.y * scaleY;
            const centerX = image.naturalWidth / 2;
            const centerY = image.naturalHeight / 2;
            const rotateRads = rotate * TO_RADIANS;
            ctx.save();
            // Move the crop origin to the canvas origin (0,0)
            ctx.translate(-cropX, -cropY);
            // Move the origin to the center of the original position
            ctx.translate(centerX, centerY);
            // Rotate around the origin
            ctx.rotate(rotateRads);
            // Scale the image
            ctx.scale(scale, scale);
            // Move the center of the image to the origin (0,0)
            ctx.translate(-centerX, -centerY);
            ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight);
            const blob = yield offscreen.convertToBlob({
                type: 'image/png',
            });
            if (props.onSave) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    props.onSave(reader.result);
                };
                reader.readAsDataURL(blob);
            }
        });
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(DialogTitle, null, t('widget.widgetFile.editImage')),
        React.createElement(DialogContent, null,
            React.createElement(ReactCrop, { crop: crop, onChange: (c, p) => setCrop(p), onComplete: (c) => setCompletedCrop(c), aspect: props.cropAspectRatio, circularCrop: props.circularCrop },
                React.createElement("img", { alt: 'Edit Image Preview', ref: imgRef, src: props.imageData, style: { transform: `scale(${scale}) rotate(${rotate}deg`, maxHeight: 'calc(100vh - 300px)' }, onLoad: onImageLoad })),
            React.createElement(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    pt: 1,
                    gap: 4,
                } },
                React.createElement(Box, { sx: { flexGrow: 1 } },
                    React.createElement(Typography, { gutterBottom: true },
                        t('widget.widgetFile.scale'),
                        ":"),
                    React.createElement(Slider, { sx: { pt: 1 }, "aria-label": t('widget.widgetFile.scale'), defaultValue: 1, step: 0.1, min: 0.1, max: 4, size: 'medium', valueLabelDisplay: 'auto', onChange: (_, newValue) => setScale(newValue), "data-test": 'crop-scale-slider' })),
                props.allowRotate && (React.createElement(Box, { sx: { flexGrow: 1 } },
                    React.createElement(Typography, { gutterBottom: true },
                        t('widget.widgetFile.rotate'),
                        ":"),
                    React.createElement(Slider, { sx: { pt: 1 }, "aria-label": t('widget.widgetFile.rotate'), defaultValue: 0, step: 1, min: -180, max: 180, size: 'medium', valueLabelDisplay: 'auto', onChange: (_, newValue) => setRotate(newValue), "data-test": 'crop-rotate-slider' }))))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { color: 'primary', onClick: () => props.onCancel && props.onCancel(), "data-test": 'cancel-crop-button' }, t('common.cancel')),
            React.createElement(Button, { color: 'primary', onClick: cropImage, "data-test": 'save-crop-button' }, t('common.save')))));
};
